import { inject, Injectable } from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { LOGO_BIO_SVG } from "../utils/bio-logo.util";

@Injectable({
  providedIn: "root",
})
export class IconService {
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);

  /** Les icônes servies par un fichier d'`assets/icons/<nom>.svg`. */
  static svgIcons = [
    "round_progress_empty",
    "round_progress_quarter",
    "round_progress_3_quarter",
    "round_progress_half",
    "round_progress_full",
    "arrow_left_circle",
    "google_icon",
    "microsoft_icon",
  ];

  /**
   * Les **marques inlinées** : leur balisage vit dans le code, pas dans un fichier.
   *
   * ⚠️ **Enregistrées ici pour qu'un NOM d'icône suffise.** Le logo AB a déjà son composant
   * (`vl-bio-logo`, un `<img>` sur data-URI) et c'est lui qu'il faut employer dans un gabarit. Mais
   * plusieurs endroits ne manipulent pas un gabarit : ils portent un nom d'icône dans une **donnée** —
   * le `iconLeft` d'un `vl-chips`, le `badge.icon` d'une option de `vl-select`, l'`icon` d'un module du
   * catalogue de widgets. Sans entrée au registre, ces trois-là ne pouvaient afficher que l'icône
   * Material `eco`, c'est-à-dire une feuille générique à la place de la marque.
   *
   * ⚠️ **Une seule source de balisage** (`LOGO_BIO_SVG`) pour les deux chemins de rendu. Un fichier
   * d'`assets` aurait été plus simple à brancher — c'est le mécanisme de `svgIcons` juste au-dessus — et
   * il aurait donné **deux** représentations de la même marque, qui divergent à la première retouche.
   *
   * ⚠️ **`addSvgIconLiteral` déplie le SVG à chaque occurrence**, là où l'`<img>` du composant pose un
   * élément et laisse le navigateur décoder l'image une fois. C'est pourquoi les tableaux denses gardent
   * `vl-bio-logo` : ce registre sert les emplois ponctuels, pas les listes.
   */
  private static readonly svgLiterals: Record<string, string> = {
    bio_ab: LOGO_BIO_SVG,
  };

  constructor() {}

  initIcons() {
    this.matIconRegistry.setDefaultFontSetClass("material-symbols-outlined");
    for (let icon of IconService.svgIcons) {
      this.matIconRegistry.addSvgIcon(
        icon,
        this.domSanitizer.bypassSecurityTrustResourceUrl(
          `assets/icons/${icon}.svg`,
        ),
      );
    }
    for (const [nom, svg] of Object.entries(IconService.svgLiterals)) {
      this.matIconRegistry.addSvgIconLiteral(
        nom,
        this.domSanitizer.bypassSecurityTrustHtml(svg),
      );
    }
  }

  static isSvgIcon(icon: string) {
    return (
      IconService.svgIcons.includes(icon) || icon in IconService.svgLiterals
    );
  }
}
